<template>
  <div :class="$style.coreAttributes">
    <h2>{{ t('listingCoreAttributes') }}</h2>
    <dl>
      <dt>{{ t('availability.AVAILABILITY') }}:</dt>
      <dd>{{ availabilityLabel }}</dd>
      <dt>{{ t('listingCategory') }}:</dt>
      <dd>{{ t(`listingCategories.${mostSpecificCategory}`) }}</dd>
      <template v-for="attribute in definedAttributes" :key="attribute.field">
        <dt>{{ t(`listingCharacteristic.${attribute.field}`) }}:</dt>
        <dd>
          {{ getAttributeDisplayValue(attribute) }}
          <span v-if="attribute.unit" v-html="attribute.unit" />
        </dd>
      </template>
    </dl>
  </div>
</template>

<script lang="ts" setup>
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';

import { getAvailability } from '@/helpers/ListingData/ListingData';
import { useI18n } from 'vue-i18n';
import { ATTRIBUTES_UNITS_OF_MEASUREMENT, AttributeValueRecord, ReAttribute } from './types';

const { t } = useI18n();

defineOptions({
  name: 'CoreAttributes',
});

interface Props {
  listing: Listing;
}

const props = defineProps<Props>();

const mostSpecificCategory = computed(() => {
  return (props.listing.categories ?? []).slice(-1)[0];
});
const definedAttributes = computed(() => {
  return attributes.value.filter((x) => isAttributeDefined(x));
});
const attributes = computed(() => {
  const attributes = (props.listing.characteristics ?? {}) as unknown as AttributeValueRecord;

  return [
    'numberOfRooms',
    'numberOfApartments',
    'numberOfBathrooms',
    'numberOfShowers',
    'floor',
    'numberOfFloors',
    'livingSpace',
    'totalFloorSpace',
    'singleFloorSpace',
    'lotSize',
    'buildingFloorSize',
    'cubage',
    'ceilingHeight',
    'hallHeight',
    'floorLoad',
    'elevatorCapacity',
    'craneCapacity',
    'grossPremium',
    'yearLastRenovated',
    'yearBuilt',
  ].map((fieldName) => ({
    field: fieldName,
    value: attributes[fieldName],
    unit: ATTRIBUTES_UNITS_OF_MEASUREMENT[fieldName],
  }));
});
const availabilityLabel = computed(() => {
  return getAvailability(props.listing, t);
});

function isAttributeDefined(attribute: ReAttribute) {
  return attribute.value !== undefined;
}
function getAttributeDisplayValue(attribute: ReAttribute) {
  if (attribute.field === 'floor' && attribute.value === 0) {
    return t('groundFloor');
  }

  return attribute.value;
}
</script>

<style module lang="scss">
.coreAttributes sup {
  line-height: 0;
}
</style>
